import type { TransientInputFile } from "./formatJobResults";
import type { StoredFile } from "./storedDirectory";

import { transientRunPrefix } from "../api/urls";
import { TRANSIENT_INPUT_FOLDER } from "../model/constants";
import { isRecord } from "./parseApiId";
import { hasFileExtension, isJsonFileName, isXlsxFileName, lastPathSegment } from "./storedDirectory";

const INPUT_FOLDER_MARKER = `/${TRANSIENT_INPUT_FOLDER}/`;
const DOWNLOADED_INPUTS_FOLDER = "downloaded inputs";

export const normalizeRelativeFolder = (value: string) => value.split("/").filter(Boolean).join("/");

export const isIgnoredInputFolder = (folderName: string) =>
  lastPathSegment(folderName).trim().toLowerCase() === DOWNLOADED_INPUTS_FOLDER;

const isDownloadedInputsPath = (relativePath: string) =>
  relativePath.split("/").some((segment) => isIgnoredInputFolder(segment));

const relativeFromInputPath = (path: string) => {
  const index = path.indexOf(INPUT_FOLDER_MARKER);
  if (index >= 0) return normalizeRelativeFolder(path.slice(index + INPUT_FOLDER_MARKER.length));
  return normalizeRelativeFolder(path);
};

const inputRelativePath = (file: StoredFile) => {
  if (file.path) return relativeFromInputPath(file.path) || lastPathSegment(file.name);
  if (file.name.includes("/")) {
    const index = file.name.indexOf(INPUT_FOLDER_MARKER);
    if (index >= 0) return file.name.slice(index + INPUT_FOLDER_MARKER.length);
    return file.name.replace(/^\/+/, "");
  }
  return lastPathSegment(file.name);
};

const isInputFileName = (name: string) => isJsonFileName(name) || isXlsxFileName(name);

export type InputFolderEntry = {
  name: string;
  path?: string;
};

export type InputDirectoryListing = {
  files: StoredFile[];
  folders: InputFolderEntry[];
};

export const parseInputFolderEntries = (value: unknown): InputFolderEntry[] => {
  const data = isRecord(value) ? (isRecord(value.data) ? value.data : value) : {};
  if (!Array.isArray(data.directories)) return [];
  return data.directories.flatMap((item) => {
    if (!isRecord(item)) return [];
    const rawName = typeof item.name === "string" ? item.name.replace(/\/+$/, "") : "";
    const rawPath = typeof item.path === "string" ? item.path : "";
    const name = lastPathSegment(rawName || rawPath);
    if (!name || isIgnoredInputFolder(name) || hasFileExtension(name)) return [];
    return [{ name, path: rawPath || undefined }];
  });
};

export const immediateInputChildFolders = (folders: InputFolderEntry[], currentRelative = "") => {
  const current = normalizeRelativeFolder(currentRelative);
  const currentParts = current ? current.split("/") : [];
  const currentLast = lastPathSegment(current).toLowerCase();
  const children = new Set<string>();

  folders.forEach((folder) => {
    if (!folder.path && folder.name.toLowerCase() === currentLast) return;
    const relative = folder.path
      ? relativeFromInputPath(folder.path)
      : normalizeRelativeFolder([current, folder.name].filter(Boolean).join("/"));
    const parts = relative.split("/").filter(Boolean);
    if (parts.length !== currentParts.length + 1) return;
    if (currentParts.some((part, index) => part.toLowerCase() !== parts[index]?.toLowerCase())) return;
    const child = parts[parts.length - 1] ?? "";
    if (!child || isIgnoredInputFolder(child) || hasFileExtension(child)) return;
    children.add(child);
  });

  return [...children];
};

export const collectInputFiles = (
  listings: { files: StoredFile[] }[],
  projectId: string,
  configId: number
): TransientInputFile[] => {
  const unique = new Map<string, TransientInputFile>();
  listings.forEach((listing) => {
    listing.files.forEach((file) => {
      const name = lastPathSegment(file.name) || lastPathSegment(file.path ?? "");
      if (!name || !hasFileExtension(name) || !isInputFileName(name)) return;
      const relativePath = inputRelativePath(file);
      if (!relativePath || isDownloadedInputsPath(relativePath)) return;
      const path = file.path || `${transientRunPrefix(projectId, configId)}/${TRANSIENT_INPUT_FOLDER}/${relativePath}`;
      unique.set(path, { name, path, relativePath, url: file.url });
    });
  });
  return [...unique.values()].sort((a, b) => a.relativePath.localeCompare(b.relativePath));
};
